import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';

import { DomainError } from '@/core/errors/DomainError';
import {
  areSettingsEditable,
  tournamentSettingsSchema,
  type TournamentSettings,
  type Tournament,
  type Uid,
} from '@/core/schemas';
import { toAppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { firestorePaths } from '@/infrastructure/firestore/paths';

/**
 * Persists the tournament settings.
 *
 * Guarded twice on purpose: the settings are validated against the schema
 * before writing, and the "only before the start" rule is checked here and
 * again in the security rules. The client check produces the better message;
 * the rule is the one that actually holds.
 */
export async function saveTournamentSettings(input: {
  tournament: Tournament;
  settings: TournamentSettings;
  actorUid: Uid;
}): Promise<void> {
  const { tournament, settings, actorUid } = input;

  if (!areSettingsEditable(tournament.status)) {
    throw new DomainError(
      'SETTINGS_LOCKED',
      'Die Einstellungen können nur vor dem Turnierstart geändert werden.',
      { status: tournament.status },
    );
  }

  const parsed = tournamentSettingsSchema.safeParse(settings);
  if (!parsed.success) {
    throw new DomainError(
      'INVALID_PHASE_CONFIG',
      'Die Einstellungen sind unvollständig oder ungültig.',
      { issues: parsed.error.issues.length },
    );
  }

  try {
    await updateDoc(doc(firestore, firestorePaths.tournament(tournament.id)), {
      settings: parsed.data,
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}
